import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import Logo from "@/components/brand/Logo";
import DemoNotice from "@/components/site/DemoNotice";

const LOGIN_BG =
    "https://images.unsplash.com/photo-1741806914412-340ca16e9175?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwYWJzdHJhY3QlMjB3aGl0ZXxlbnwwfHx8fDE3ODMxMTg3NDd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/app/dashboard";

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email.trim().toLowerCase(), password);
        setLoading(false);
        if (res.ok) navigate(from, { replace: true });
        else setError(res.error);
    };

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
            {/* Left visual */}
            <div className="relative hidden lg:block">
                <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-white/80 via-white/10 to-transparent" />
                <div className="relative z-10 flex h-full flex-col justify-between p-10">
                    <div className="flex items-center gap-2">
                        <Logo size={32} />
                        <span className="font-display text-lg font-semibold">YugOS</span>
                    </div>
                    <div className="max-w-md">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-700">
                            Commerce Intelligence Platform
                        </div>
                        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-zinc-950 leading-tight">
                            The operating system for modern wholesale and retail businesses.
                        </h2>
                        <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                            Understand the health of your business in 30 seconds. Onboard a new
                            employee in 30 minutes.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center px-6 py-12 sm:px-12">
                <div className="w-full max-w-sm">
                    <div className="mb-8 lg:hidden flex items-center gap-2">
                        <Logo size={28} />
                        <span className="font-display text-base font-semibold">YugOS</span>
                    </div>

                    <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-500">
                        Welcome back
                    </div>
                    <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight text-zinc-950">
                        Log in to YugOS
                    </h1>
                    <p className="mt-2 text-sm text-zinc-500">
                        Access the intelligence layer for Yug Traders.
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-xs font-medium">
                                Work email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@yugtraders.com"
                                data-testid="login-email"
                                className="h-10"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="text-xs font-medium">
                                    Password
                                </Label>
                                <Link
                                    to="/forgot-password"
                                    className="text-xs text-zinc-600 hover:text-zinc-900 underline underline-offset-4"
                                    data-testid="login-forgot-link"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            <Input
                                id="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                data-testid="login-password"
                                className="h-10"
                            />
                        </div>

                        {error && (
                            <div
                                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            className="w-full h-10"
                            disabled={loading}
                            data-testid="login-submit-button"
                        >
                            {loading && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
                            {loading ? "Signing in…" : "Sign in"}
                        </Button>

                        <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-3 py-2.5 text-[11px] text-zinc-600">
                            <div className="font-medium text-zinc-800">Internal application</div>
                            <div className="mt-0.5">
                                Accounts are provisioned by administrators. Contact your admin for access.
                            </div>
                        </div>

                        <DemoNotice />
                    </form>

                    <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px] text-zinc-500">
                        <div>© {new Date().getFullYear()} YugOS · v0.1</div>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/privacy" className="hover:text-zinc-900 transition-colors" data-testid="login-privacy">
                                Privacy
                            </Link>
                            <Link to="/terms" className="hover:text-zinc-900 transition-colors" data-testid="login-terms">
                                Terms
                            </Link>
                            <Link to="/contact" className="hover:text-zinc-900 transition-colors" data-testid="login-contact">
                                Contact
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
